import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useMemo,
} from "react";
import columnOrderStorage from "../utils/columnOrderStorage";

const ALL_TABLE_ACTIONS = {
  SET_COLUMN_VISIBILITY: "SET_COLUMN_VISIBILITY",
  SET_CURRENT_PAGE: "SET_CURRENT_PAGE",
  SET_ROWS_PER_PAGE: "SET_ROWS_PER_PAGE",
  SET_SORTING: "SET_SORTING",
  SET_FILTERS: "SET_FILTERS",
  SET_COLUMNS_VISIBLE: "SET_COLUMNS_VISIBLE",
  SET_HEADERS_ROTATED: "SET_HEADERS_ROTATED",
  SET_DATA_ENCRYPTED: "SET_DATA_ENCRYPTED",
  SET_PINNED_COLUMNS: "SET_PINNED_COLUMNS",
  TOGGLE_PINNED_COLUMN: "TOGGLE_PINNED_COLUMN",
  SET_ACTIVE_COLUMNS: "SET_ACTIVE_COLUMNS",
  TOGGLE_COLUMN_WIDTH: "TOGGLE_COLUMN_WIDTH",
  SET_COLUMN_CONDITIONS: "SET_COLUMN_CONDITIONS",
  UPDATE_COLUMN_CONDITION: "UPDATE_COLUMN_CONDITION",
  SET_HOVERED_COLUMN: "SET_HOVERED_COLUMN",
  SET_COLUMN_ORDER: "SET_COLUMN_ORDER",
  RESET_COLUMN_ORDER: "RESET_COLUMN_ORDER",

  SET_COLUMN_SIZING: "SET_COLUMN_SIZING", // Primary for setting/replacing all sizing
  UPDATE_COLUMN_SIZING: "UPDATE_COLUMN_SIZING", // For merging/updating multiple column sizes
  SET_UPPER_VALUE: "SET_UPPER_VALUE",
  SET_LOWER_VALUE: "SET_LOWER_VALUE",
  SET_LAST_VALID_HOVERED_COLUMN: "SET_LAST_VALID_HOVERED_COLUMN",
  SET_IS_PANEL_STICKY: "SET_IS_PANEL_STICKY",
  SET_IS_PANEL_HOVERED: "SET_IS_PANEL_HOVERED",
  SET_CONTROLS_POSITION: "SET_CONTROLS_POSITION",
  SET_EDITING_CELL: "SET_EDITING_CELL",
  SET_TEMP_VALUE: "SET_TEMP_VALUE",
  SET_TEMP_COLUMN_SIZING: "SET_TEMP_COLUMN_SIZING",
  MERGE_TEMP_COLUMN_SIZING: "MERGE_TEMP_COLUMN_SIZING",
  SET_REFETCH_RECRUITS_TRIGGER: "SET_REFETCH_RECRUITS_TRIGGER",
  INCREMENT_REFETCH_TRIGGER: "INCREMENT_REFETCH_TRIGGER",
  SET_FORMULAS_MAPPING: "SET_FORMULAS_MAPPING",
  UPDATE_FORMULAS_MAPPING: "UPDATE_FORMULAS_MAPPING",
  RESET_EDITING_CELL: "RESET_EDITING_CELL",
};

const initialState = {
  columnVisibility: {},
  currentPage: 1,
  rowsPerPage: 1000000000,
  sorting: [],
  filters: { filter: "" },
  columnsVisible: true,
  headersRotated: false,
  dataEncrypted: false,
  pinnedColumns: [],
  columnSizing: {}, // This will be the single source of truth for columnSizing
  activeColumns: {},
  columnConditions: {},
  hoveredColumn: null,
  columnOrder: [],

  upperValue: 10,
  lowerValue: 5,
  lastValidHoveredColumn: null,
  isPanelSticky: false,
  isPanelHovered: false,
  controlsPosition: { left: "50%" },
  editingCell: { rowId: null, columnId: null },
  tempValue: "",
  tempColumnSizing: {},
  refetchRecruitsTrigger: 0,
  formulasMapping: {},
};

function tableReducer(state, action) {
  switch (action.type) {
    case ALL_TABLE_ACTIONS.SET_COLUMN_VISIBILITY:
      console.log("Reducer updating column visibility:", action.payload);
      return { ...state, columnVisibility: action.payload };
    case ALL_TABLE_ACTIONS.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case ALL_TABLE_ACTIONS.SET_ROWS_PER_PAGE:
      return { ...state, rowsPerPage: action.payload };
    case ALL_TABLE_ACTIONS.SET_SORTING:
      return { ...state, sorting: action.payload };
    case ALL_TABLE_ACTIONS.SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case ALL_TABLE_ACTIONS.SET_COLUMNS_VISIBLE:
      return { ...state, columnsVisible: action.payload };
    case ALL_TABLE_ACTIONS.SET_HEADERS_ROTATED:
      return { ...state, headersRotated: action.payload };
    case ALL_TABLE_ACTIONS.SET_DATA_ENCRYPTED:
      return { ...state, dataEncrypted: action.payload };
    case ALL_TABLE_ACTIONS.SET_PINNED_COLUMNS:
      return { ...state, pinnedColumns: action.payload };
    case ALL_TABLE_ACTIONS.TOGGLE_PINNED_COLUMN: {
      const { columnId } = action.payload;
      const isPinned = state.pinnedColumns.includes(columnId);
      const MAX_PINNED_COLUMNS = 30;
      let newPinnedColumns = [...state.pinnedColumns];
      if (isPinned) {
        newPinnedColumns = newPinnedColumns.filter((id) => id !== columnId);
      } else if (newPinnedColumns.length < MAX_PINNED_COLUMNS) {
        newPinnedColumns.push(columnId);
      }
      return { ...state, pinnedColumns: newPinnedColumns };
    }
    case ALL_TABLE_ACTIONS.SET_ACTIVE_COLUMNS:
      return { ...state, activeColumns: action.payload };
    case ALL_TABLE_ACTIONS.TOGGLE_COLUMN_WIDTH: {
      const { columnId, isActive } = action.payload;
      const defaultWidth = 150;
      return {
        ...state,
        activeColumns: { ...state.activeColumns, [columnId]: isActive },
        columnSizing: {
          ...state.columnSizing,
          [columnId]: isActive ? 30 : defaultWidth,
        },
      };
    }
    case ALL_TABLE_ACTIONS.SET_COLUMN_CONDITIONS:
      return { ...state, columnConditions: action.payload };
    case ALL_TABLE_ACTIONS.UPDATE_COLUMN_CONDITION: {
      const { columnId, conditions } = action.payload;
      return {
        ...state,
        columnConditions: {
          ...state.columnConditions,
          [columnId]: { ...state.columnConditions[columnId], ...conditions },
        },
      };
    }
    case ALL_TABLE_ACTIONS.SET_HOVERED_COLUMN:
      return { ...state, hoveredColumn: action.payload };
    case ALL_TABLE_ACTIONS.SET_COLUMN_ORDER:
      return { ...state, columnOrder: action.payload };
    case ALL_TABLE_ACTIONS.RESET_COLUMN_ORDER: {
      const { tableId, defaultOrder } = action.payload;
      columnOrderStorage.resetColumnOrder(tableId);
      return { ...state, columnOrder: defaultOrder };
    }

    case ALL_TABLE_ACTIONS.SET_COLUMN_SIZING: // Handles full replacement, supports function payload
      return {
        ...state,
        columnSizing:
          typeof action.payload === "function"
            ? action.payload(state.columnSizing)
            : action.payload,
      };
    case ALL_TABLE_ACTIONS.UPDATE_COLUMN_SIZING: // Handles merging/updating specific column sizes
      return {
        ...state,
        columnSizing: {
          ...state.columnSizing,
          ...action.payload, // e.g., { columnId1: newSize1, columnId2: newSize2 }
        },
      };
    case ALL_TABLE_ACTIONS.SET_UPPER_VALUE:
      return { ...state, upperValue: action.payload };
    case ALL_TABLE_ACTIONS.SET_LOWER_VALUE:
      return { ...state, lowerValue: action.payload };
    case ALL_TABLE_ACTIONS.SET_LAST_VALID_HOVERED_COLUMN:
      if (state.lastValidHoveredColumn === action.payload) {
        return state;
      }
      return { ...state, lastValidHoveredColumn: action.payload };
    case ALL_TABLE_ACTIONS.SET_IS_PANEL_STICKY:
      return { ...state, isPanelSticky: action.payload };
    case ALL_TABLE_ACTIONS.SET_IS_PANEL_HOVERED:
      return { ...state, isPanelHovered: action.payload };
    case ALL_TABLE_ACTIONS.SET_CONTROLS_POSITION:
      return {
        ...state,
        controlsPosition:
          typeof action.payload === "function"
            ? action.payload(state.controlsPosition)
            : action.payload,
      };
    case ALL_TABLE_ACTIONS.SET_EDITING_CELL:
      return { ...state, editingCell: action.payload };
    case ALL_TABLE_ACTIONS.RESET_EDITING_CELL:
      return {
        ...state,
        editingCell: { rowId: null, columnId: null },
        tempValue: "",
      };
    case ALL_TABLE_ACTIONS.SET_TEMP_VALUE:
      return { ...state, tempValue: action.payload };
    case ALL_TABLE_ACTIONS.SET_TEMP_COLUMN_SIZING:
      return {
        ...state,
        tempColumnSizing:
          typeof action.payload === "function"
            ? action.payload(state.tempColumnSizing)
            : action.payload,
      };
    case ALL_TABLE_ACTIONS.MERGE_TEMP_COLUMN_SIZING:
      return {
        ...state,
        tempColumnSizing: { ...state.tempColumnSizing, ...action.payload },
      };
    case ALL_TABLE_ACTIONS.SET_REFETCH_RECRUITS_TRIGGER:
      return { ...state, refetchRecruitsTrigger: action.payload };
    case ALL_TABLE_ACTIONS.INCREMENT_REFETCH_TRIGGER:
      return {
        ...state,
        refetchRecruitsTrigger: state.refetchRecruitsTrigger + 1,
      };
    case ALL_TABLE_ACTIONS.SET_FORMULAS_MAPPING:
      return {
        ...state,
        formulasMapping:
          typeof action.payload === "function"
            ? action.payload(state.formulasMapping)
            : action.payload,
      };
    case ALL_TABLE_ACTIONS.UPDATE_FORMULAS_MAPPING:
      return {
        ...state,
        formulasMapping: { ...state.formulasMapping, ...action.payload },
      };

    default:
      return state;
  }
}

// Create the context
const TableContext = createContext();

// Custom hook to use the table context
export function useTableContext() {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error("useTableContext must be used within a TableProvider");
  }
  return context;
}

// Provider component
export function TableProvider({
  children,
  tableId,
  initialVisibility = {},
  initialColumns = [],
  externalColumnVisibility,
  externalSetColumnVisibility,
}) {
  const [state, dispatch] = useReducer(tableReducer, {
    ...initialState,
    columnVisibility: externalColumnVisibility || initialVisibility,
    columnOrder:
      initialColumns.length > 0
        ? typeof initialColumns[0] === "string"
          ? initialColumns
          : initialColumns.map((col) => col.id || col)
        : [],
  });

  useEffect(() => {
    if (externalColumnVisibility) {
      console.log(
        "TableProvider: External visibility changed:",
        externalColumnVisibility
      );
      dispatch({
        type: ALL_TABLE_ACTIONS.SET_COLUMN_VISIBILITY,
        payload: externalColumnVisibility,
      });
    }
  }, [externalColumnVisibility]);

  useEffect(() => {
    const savedOrder = columnOrderStorage.getColumnOrder(tableId);
    if (savedOrder && savedOrder.length > 0) {
      dispatch({
        type: ALL_TABLE_ACTIONS.SET_COLUMN_ORDER,
        payload: savedOrder,
      });
    }
  }, [tableId]);

  useEffect(() => {
    if (state.columnOrder.length > 0) {
      columnOrderStorage.setColumnOrder(tableId, state.columnOrder);
    }
  }, [state.columnOrder, tableId]);

  const contextValue = useMemo(() => {
    return {
      state,
      // dispatch, // Exposing dispatch directly is an option, but typically methods are preferred

      // Column visibility methods
      setColumnVisibility: (visibilityOrFn) => {
        const newVisibility =
          typeof visibilityOrFn === "function"
            ? visibilityOrFn(externalColumnVisibility || state.columnVisibility)
            : visibilityOrFn;
        console.log(
          "TableContext: setColumnVisibility called with:",
          newVisibility
        );
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_COLUMN_VISIBILITY,
          payload: newVisibility,
        });
        if (externalSetColumnVisibility) {
          console.log("TableContext: Propagating to external state");
          externalSetColumnVisibility(newVisibility);
        }
      },

      // Pagination methods
      setCurrentPage: (page) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_CURRENT_PAGE, payload: page }),
      setRowsPerPage: (rows) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_ROWS_PER_PAGE, payload: rows }),

      // Sorting methods
      setSorting: (sorting) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_SORTING, payload: sorting }),

      // Filtering methods
      setFilters: (filters) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_FILTERS, payload: filters }),

      // Column display methods
      setColumnsVisible: (visible) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_COLUMNS_VISIBLE,
          payload: visible,
        }),
      setHeadersRotated: (rotated) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_HEADERS_ROTATED,
          payload: rotated,
        }),
      setDataEncrypted: (encrypted) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_DATA_ENCRYPTED,
          payload: encrypted,
        }),

      // Column customization methods
      setPinnedColumns: (columns) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_PINNED_COLUMNS,
          payload: columns,
        }),
      togglePinnedColumn: (columnId) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.TOGGLE_PINNED_COLUMN,
          payload: { columnId },
        }),

      // Sets the entire column sizing state, optionally with a function
      setColumnSizing: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_COLUMN_SIZING, payload }),
      // Updates/merges sizes for one or more columns
      updateColumnSizing: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.UPDATE_COLUMN_SIZING, payload }),
      // Convenience method for updating a single column's size (uses UPDATE_COLUMN_SIZING)
      updateSingleColumnSize: (columnId, width) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.UPDATE_COLUMN_SIZING,
          payload: { [columnId]: width },
        }),

      setActiveColumns: (active) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_ACTIVE_COLUMNS,
          payload: active,
        }),
      toggleColumnWidth: (columnId, isActive) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.TOGGLE_COLUMN_WIDTH,
          payload: { columnId, isActive },
        }),

      // Column conditions methods
      setColumnConditions: (conditions) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_COLUMN_CONDITIONS,
          payload: conditions,
        }),
      updateColumnCondition: (columnId, conditions) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.UPDATE_COLUMN_CONDITION,
          payload: { columnId, conditions },
        }),

      // Hover methods
      setHoveredColumn: (column) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_HOVERED_COLUMN,
          payload: column,
        }),

      // Column order methods
      setColumnOrder: (order) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_COLUMN_ORDER, payload: order }),
      resetColumnOrder: (defaultOrder) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.RESET_COLUMN_ORDER,
          payload: { tableId, defaultOrder },
        }),

      // Methods from useTableState.js
      setUpperValue: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_UPPER_VALUE, payload }),
      setLowerValue: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_LOWER_VALUE, payload }),
      setLastValidHoveredColumn: (payload) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_LAST_VALID_HOVERED_COLUMN,
          payload,
        }),
      setIsPanelSticky: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_IS_PANEL_STICKY, payload }),
      setIsPanelHovered: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_IS_PANEL_HOVERED, payload }),
      setControlsPosition: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_CONTROLS_POSITION, payload }),
      setEditingCell: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_EDITING_CELL, payload }),
      resetEditingCell: () =>
        dispatch({ type: ALL_TABLE_ACTIONS.RESET_EDITING_CELL }),
      setTempValue: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_TEMP_VALUE, payload }),
      setTempColumnSizing: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_TEMP_COLUMN_SIZING, payload }),
      mergeTempColumnSizing: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.MERGE_TEMP_COLUMN_SIZING, payload }),
      setRefetchRecruitsTrigger: (payload) =>
        dispatch({
          type: ALL_TABLE_ACTIONS.SET_REFETCH_RECRUITS_TRIGGER,
          payload,
        }),
      incrementRefetchTrigger: () =>
        dispatch({ type: ALL_TABLE_ACTIONS.INCREMENT_REFETCH_TRIGGER }),
      setFormulasMapping: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.SET_FORMULAS_MAPPING, payload }),
      updateFormulasMapping: (payload) =>
        dispatch({ type: ALL_TABLE_ACTIONS.UPDATE_FORMULAS_MAPPING, payload }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, tableId, externalColumnVisibility, externalSetColumnVisibility]); // dispatch is stable

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
}

export { ALL_TABLE_ACTIONS };
export default TableContext;
